import { Trans } from 'react-i18next';
import { ReactElement, RefObject, useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Device, DeviceTeam, DeviceUser } from '@/api';
import {
    UIAvatar,
    UIButton,
    UICheckbox,
    UIEmptyState,
    UIExternalLinkButton,
    UIFilterGroup,
    UIFormInput,
    UIIcon,
    UIMultiFilterGroup,
    UIMultiFilterItem,
    UIPopover,
    UISection,
    UITableHeaderCell,
    UITableSortDirection,
    UIText,
    UITextButton,
    UITooltip
} from '@/ui';
import { blue, fontColorLight } from '@/ui/styles';
import lwdImage from '../assets/images/lwd.png';
import useElementSize from '@/common/hooks/use-element-size';
import { useCompanyPermissions, useTeamPermissions } from '@/companies/hooks';
import { useDevices } from '../providers';
import { useDevicesSearch, useDevicesTranslation } from '../hooks';
import { BulkActionBar } from './bulk-action-bar';
import { DeviceFeaturesIcons } from './device-features-icons';
import { DeviceRowActionsMenu } from './device-row-actions-menu';
import { DeviceStatus } from './device-status';
import { ScanDeviceModal } from './scan-device-modal';
import { UpdateDeviceModal } from './update-device-modal';
import styles from './devices-list.module.scss';

const BUY_DEVICES_URL =
    'https://www.safepointapp.com/hardware/lone-worker-device-with-sim';

type DevicesListProps = {
    companyId: string;
    teamId?: string;
    /** Opens the activation modal from the empty state (main devices page only). */
    onActivateDevice?: () => void;
};
export function DevicesList(props: DevicesListProps): ReactElement {
    const { companyId, teamId, onActivateDevice } = props;
    const { t } = useDevicesTranslation();
    const { canEditDevices: canEditCompanyDevices } = useCompanyPermissions();
    const { canEditDevices: canEditTeamDevices } = useTeamPermissions({
        teamId: teamId ?? ''
    });
    // The main devices list is owner/admin only; the team-scoped tab also lets
    // that team's admins edit.
    const canEditDevices = teamId ? canEditTeamDevices : canEditCompanyDevices;

    const { devices: allDevices } = useDevices(companyId);
    const devices = useMemo(
        () =>
            teamId
                ? allDevices.filter((d) => d.team?.teamId === teamId)
                : allDevices,
        [allDevices, teamId]
    );

    // When scoped to a single team, the Team column/filter are redundant.
    const hideTeam = !!teamId;

    // Bulk selection (owner/admin only).
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const selectedDevices = useMemo(
        () => devices.filter((d) => selectedIds.has(d.id)),
        [devices, selectedIds]
    );

    function toggleSelect(id: string) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }
    function clearSelection() {
        setSelectedIds(new Set());
    }

    const {
        searchQuery,
        setSearchQuery,
        searchedDevices,
        clearSearch,
        groups,
        selectedCount,
        onFiltersChanged
    } = useDevicesSearch(devices, { hideTeam });

    const [sortBy, setSortBy] = useState<SortBy>('name');
    const [sortDirection, setSortDirection] =
        useState<UITableSortDirection>('asc');

    const sortedDevices = useMemo(() => {
        function getTeamName(team: DeviceTeam | null): string {
            if (!team) {
                return t('shared.devices.no_team');
            }
            return team.name ?? 'Unknown';
        }
        function getUserName(user: DeviceUser | null): string {
            if (!user) {
                return t('shared.devices.no_member');
            }
            if (!user.joined) {
                return user.email;
            }
            return `${user.firstName} ${user.lastName}`;
        }

        return searchedDevices.sort((a, b) => {
            if (sortBy === 'name') {
                if (sortDirection === 'asc') {
                    return a.name.localeCompare(b.name);
                } else {
                    return b.name.localeCompare(a.name);
                }
            }

            if (sortBy === 'team') {
                const teamA = getTeamName(a.team);
                const teamB = getTeamName(b.team);
                if (sortDirection === 'asc') {
                    return teamA.localeCompare(teamB);
                } else {
                    return teamB.localeCompare(teamA);
                }
            }

            if (sortBy === 'user') {
                const userA = getUserName(a.user);
                const userB = getUserName(b.user);
                if (sortDirection === 'asc') {
                    return userA.localeCompare(userB);
                } else {
                    return userB.localeCompare(userA);
                }
            }

            return 0;
        });
    }, [searchedDevices, sortBy, sortDirection, t]);

    // Select-all operates over the currently visible (filtered/searched) rows.
    const visibleIds = useMemo(
        () => sortedDevices.map((d) => d.id),
        [sortedDevices]
    );
    const { allVisibleSelected, someVisibleSelected } = useMemo(() => {
        let selectedCount = 0;
        for (const id of visibleIds) {
            if (selectedIds.has(id)) {
                selectedCount++;
            }
        }
        return {
            allVisibleSelected:
                visibleIds.length > 0 && selectedCount === visibleIds.length,
            someVisibleSelected: selectedCount > 0
        };
    }, [visibleIds, selectedIds]);
    function toggleSelectAll() {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                visibleIds.forEach((id) => next.delete(id));
            } else {
                visibleIds.forEach((id) => next.add(id));
            }
            return next;
        });
    }

    if (devices.length === 0) {
        return (
            <DevicesEmptyState
                teamScoped={!!teamId}
                canEdit={canEditDevices}
                onActivate={onActivateDevice}
            />
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <DevicesSearch
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                groups={groups}
                selectedCount={selectedCount}
                onFiltersChanged={onFiltersChanged}
            />
            {canEditDevices && selectedIds.size > 0 && (
                <BulkActionBar
                    companyId={companyId}
                    devices={selectedDevices}
                    onClear={clearSelection}
                />
            )}
            {sortedDevices.length === 0 && (
                <UIEmptyState
                    description={t('shared.labels.no_results')}
                    buttonText={t('shared.labels.clear_search')}
                    emoji="😔"
                    onClick={clearSearch}
                />
            )}
            {sortedDevices.length > 0 && (
                <UISection>
                    <table className={styles.deviceTable}>
                        <thead>
                            <DeviceTableHeader
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                selectable={canEditDevices}
                                hideTeam={hideTeam}
                                allSelected={allVisibleSelected}
                                someSelected={
                                    someVisibleSelected && !allVisibleSelected
                                }
                                onToggleAll={toggleSelectAll}
                                onClick={(newSortBy, newSortDirection) => {
                                    setSortBy(newSortBy);
                                    setSortDirection(newSortDirection);
                                }}
                            />
                        </thead>
                        <tbody>
                            {sortedDevices.map((device) => (
                                <DeviceTableRow
                                    key={device.id}
                                    device={device}
                                    companyId={companyId}
                                    teamId={teamId}
                                    selectable={canEditDevices}
                                    hideTeam={hideTeam}
                                    selected={selectedIds.has(device.id)}
                                    onToggleSelect={() =>
                                        toggleSelect(device.id)
                                    }
                                />
                            ))}
                        </tbody>
                    </table>
                </UISection>
            )}
        </div>
    );
}

type DevicesSearchProps = {
    searchQuery: string;
    onSearchQueryChange: (query: string) => void;
    groups: UIMultiFilterGroup[];
    selectedCount: number;
    onFiltersChanged: (newGroups: UIMultiFilterGroup[]) => void;
};

function DevicesSearch(props: DevicesSearchProps): ReactElement {
    const {
        searchQuery,
        onSearchQueryChange,
        groups,
        selectedCount,
        onFiltersChanged
    } = props;
    const { t } = useDevicesTranslation();

    const [isScanModalOpen, setIsScanModalOpen] = useState(false);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                <UIFilterGroup>
                    <UIMultiFilterItem
                        icon="sliders"
                        text={t('shared.labels.filter')}
                        groups={groups}
                        selectedCount={selectedCount}
                        onChanged={onFiltersChanged}
                    />
                </UIFilterGroup>
                <UIFormInput
                    value={searchQuery}
                    placeholder={t('shared.labels.search')}
                    height={20}
                    onChange={onSearchQueryChange}
                />
                <UITooltip
                    text={t('components.devices_list.scan_qr_tooltip')}
                    place="right"
                >
                    <UIButton
                        leftIcon="camera"
                        onClick={() => {
                            setIsScanModalOpen(true);
                        }}
                        customHeight={42}
                    />
                </UITooltip>
            </div>
            <ScanDeviceModal
                isOpen={isScanModalOpen}
                onClose={() => setIsScanModalOpen(false)}
                onScannedDeviceId={(deviceId) => {
                    onSearchQueryChange(deviceId);
                }}
            />
        </>
    );
}

type DevicesEmptyStateProps = {
    /** True on the team Devices tab (scoped to a single team). */
    teamScoped: boolean;
    canEdit: boolean;
    /** Opens the activation modal; only used on the main devices page. */
    onActivate?: () => void;
};

/**
 * Shown when the devices list has no rows. On the main page it nudges the user
 * to activate or buy a device; on a team's Devices tab it just explains that
 * assigned devices will appear there (assigning happens elsewhere).
 */
function DevicesEmptyState(props: DevicesEmptyStateProps): ReactElement {
    const { teamScoped, canEdit, onActivate } = props;
    const { t } = useDevicesTranslation();

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: 400,
                justifyContent: 'center',
                alignItems: 'center',
                gap: 20,
                textAlign: 'center'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    background: blue
                }}
            >
                <img
                    src={lwdImage}
                    alt=""
                    style={{ height: 96, width: 'auto' }}
                />
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                    maxWidth: 360
                }}
            >
                <UIText variant="h2" bold textAlign="center">
                    {teamScoped
                        ? t('components.devices_list.empty_team_title')
                        : t('components.devices_list.empty_title')}
                </UIText>
                <UIText type="default2" textAlign="center">
                    {teamScoped
                        ? t('components.devices_list.empty_team_description')
                        : t('components.devices_list.empty_description')}
                </UIText>
            </div>

            {!teamScoped && (
                <div style={{ display: 'flex', gap: 12 }}>
                    {canEdit && onActivate && (
                        <UIButton
                            variant="primary"
                            leftIcon="zap"
                            text={t('shared.devices.activate_device_label')}
                            onClick={onActivate}
                        />
                    )}
                    <UIExternalLinkButton
                        href={BUY_DEVICES_URL}
                        // Primary when there's no Activate button beside it (e.g.
                        // no device subscription yet).
                        variant={canEdit && onActivate ? 'default' : 'primary'}
                        leftIcon="shopping-cart"
                        text={t('components.devices_list.empty_buy')}
                    />
                </div>
            )}
        </div>
    );
}

type SortBy = 'name' | 'team' | 'user';

function DeviceTableHeader(props: {
    sortBy: SortBy;
    sortDirection: UITableSortDirection;
    selectable: boolean;
    hideTeam: boolean;
    allSelected: boolean;
    someSelected: boolean;
    onToggleAll: () => void;
    onClick: (sortBy: SortBy, sortDirection: UITableSortDirection) => void;
}): ReactElement {
    const {
        sortBy,
        sortDirection,
        selectable,
        hideTeam,
        allSelected,
        someSelected,
        onToggleAll,
        onClick
    } = props;
    const { t } = useDevicesTranslation();
    return (
        <tr>
            {selectable && (
                <th className={styles.checkboxCell}>
                    <UICheckbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={onToggleAll}
                    />
                </th>
            )}
            <UITableHeaderCell
                text={t('shared.fields.name.label')}
                sortable
                sortDirection={sortBy === 'name' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('name', newSortDirection);
                }}
            />
            {!hideTeam && (
                <UITableHeaderCell
                    text={t('shared.fields.team.label')}
                    tooltip={t('components.devices_list.column_team_tooltip')}
                    sortable
                    sortDirection={
                        sortBy === 'team' ? sortDirection : undefined
                    }
                    onClick={(newSortDirection) => {
                        onClick('team', newSortDirection);
                    }}
                />
            )}
            <UITableHeaderCell
                text={t('shared.fields.member.label')}
                tooltip={t('components.devices_list.column_member_tooltip')}
                sortable
                sortDirection={sortBy === 'user' ? sortDirection : undefined}
                onClick={(newSortDirection) => {
                    onClick('user', newSortDirection);
                }}
            />
            <UITableHeaderCell
                text={t('shared.devices.features_label')}
                tooltip={t('components.devices_list.column_features_tooltip')}
            />
            <UITableHeaderCell
                text={t('shared.labels.status')}
                tooltip={t('components.devices_list.column_status_tooltip')}
            />
            <th />
        </tr>
    );
}

function DeviceTableRow(props: {
    device: Device;
    companyId: string;
    /** When set, the row is in a team's Devices tab and opens the team-scoped
        device page so the breadcrumb stays within the team. */
    teamId?: string;
    selectable: boolean;
    hideTeam: boolean;
    selected: boolean;
    onToggleSelect: () => void;
}): ReactElement {
    const {
        device,
        companyId,
        teamId,
        selectable,
        hideTeam,
        selected,
        onToggleSelect
    } = props;
    const { t } = useDevicesTranslation();
    const navigate = useNavigate();

    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

    const devicePath = teamId
        ? `/company/${companyId}/settings/teams/${teamId}/devices/${device.id}`
        : `/company/${companyId}/settings/devices/${device.id}`;

    return (
        <>
            <tr
                className={styles.tableRow}
                onClick={() => {
                    void navigate(devicePath);
                }}
            >
                {selectable && (
                    <td
                        className={styles.checkboxCell}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <UICheckbox
                            checked={selected}
                            onChange={onToggleSelect}
                        />
                    </td>
                )}
                <td>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8
                        }}
                    >
                        <UIAvatar
                            img={device.image}
                            alt={device.name}
                            size={44}
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText medium>{device.name}</UIText>
                            <UIText variant="small" type="default">
                                <Trans
                                    t={t}
                                    i18nKey="shared.devices.id"
                                    values={{ id: device.id }}
                                    components={{
                                        v: (
                                            <UIText
                                                variant="small"
                                                type="default2"
                                            />
                                        )
                                    }}
                                />
                            </UIText>
                        </div>
                    </div>
                </td>
                {!hideTeam && (
                    <td>
                        {device.team ? (
                            <TeamCell
                                team={device.team}
                                companyId={companyId}
                            />
                        ) : (
                            <NoTeamCell
                                onClick={() => {
                                    setIsUpdateModalOpen(true);
                                }}
                            />
                        )}
                    </td>
                )}
                <td>
                    {device.user ? (
                        <UserCell user={device.user} companyId={companyId} />
                    ) : (
                        <NoUserCell />
                    )}
                </td>
                <td>
                    <FeatureCell device={device} />
                </td>
                <td>
                    <StatusCell device={device} />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    {/* The row menu only offers edit actions, so hide it for
                        users who can't edit devices in this context (e.g. team
                        members viewing their team's Devices tab). */}
                    {selectable && (
                        <DeviceRowActionsMenu
                            device={device}
                            companyId={companyId}
                            onUpdate={() => setIsUpdateModalOpen(true)}
                        />
                    )}
                </td>
            </tr>
            <UpdateDeviceModal
                device={device}
                isOpen={isUpdateModalOpen}
                onClose={() => setIsUpdateModalOpen(false)}
            />
        </>
    );
}

/**
 * Cells
 */

function NoTeamCell(props: { onClick: () => void }): ReactElement {
    const { onClick } = props;
    const { t } = useDevicesTranslation();
    return (
        <UITextButton
            variant="danger"
            leftIcon="alert-triangle"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
        >
            {t('shared.devices.no_team')}
        </UITextButton>
    );
}

function TeamCell(props: {
    team: DeviceTeam;
    companyId: string;
}): ReactElement {
    const { team, companyId } = props;
    const { t } = useDevicesTranslation();

    const [divRef, size] = useElementSize<HTMLDivElement>();

    const popoverContent = useCallback(
        (divRef?: RefObject<HTMLDivElement | null>) => (
            <div
                ref={divRef}
                style={{
                    display: 'flex',
                    padding: 8,
                    gap: 16,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <UIAvatar
                    size={40}
                    img={team.image ?? ''}
                    alt={`${team.name}`}
                />
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column'
                    }}
                >
                    <UIText type="default">{`${team.name}`}</UIText>
                </div>
                <div>
                    <Link
                        to={`/company/${companyId}/settings/teams/${team.teamId}`}
                        style={{ textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <UIButton>{t('shared.labels.open')}</UIButton>
                    </Link>
                </div>
            </div>
        ),
        [team, companyId, t]
    );

    return (
        <>
            <UIPopover
                placement="bottom_left"
                trigger="hover"
                width={size.width}
                triggerNode={
                    <div>
                        <UIText type="link">{`${team.name}`}</UIText>
                    </div>
                }
            >
                {popoverContent()}
            </UIPopover>
            {/* Dummy to get width of popover content */}
            <div
                style={{
                    position: 'absolute',
                    opacity: 0,
                    pointerEvents: 'none'
                }}
            >
                {popoverContent(divRef)}
            </div>
        </>
    );
}

function NoUserCell(): ReactElement {
    const { t } = useDevicesTranslation();
    return <UIText type="default2">{t('shared.devices.no_member')}</UIText>;
}

function UserCell(props: {
    user: DeviceUser;
    companyId: string;
}): ReactElement {
    const { user } = props;
    const { t } = useDevicesTranslation();

    const [divRef, size] = useElementSize<HTMLDivElement>();

    const [image, displayName] = useMemo(() => {
        const img = user.image ?? '';
        const name = user.joined
            ? `${user.firstName} ${user.lastName}`
            : user.email;
        return [img, name];
    }, [user]);

    const popoverContent = useCallback(
        (divRef?: RefObject<HTMLDivElement | null>) => (
            <div
                ref={divRef}
                style={{
                    display: 'flex',
                    padding: 8,
                    gap: 16,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <UIAvatar size={40} img={image} alt={displayName} />
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <UIText type="default">{displayName}</UIText>
                    {user.joined ? (
                        <UIText type="default2">{`${user.email}`}</UIText>
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                gap: 4,
                                alignItems: 'center'
                            }}
                        >
                            <UIIcon
                                name="send"
                                size={16}
                                color={fontColorLight}
                            />
                            <UIText type="default2">
                                {t('components.devices_list.invited')}
                            </UIText>
                        </div>
                    )}
                </div>
                {/* <div>
                    <Link
                        to={`/company/${companyId}/settings/members/${user.companyUserId}`}
                        style={{ textDecoration: 'none' }}
                    >
                        <UIButton>Open</UIButton>
                    </Link>
                </div> */}
            </div>
        ),
        [user, displayName, image, t]
    );

    return (
        <>
            <UIPopover
                placement="bottom_left"
                trigger="hover"
                width={size.width}
                triggerNode={
                    <div>
                        <UIText type="link">{displayName}</UIText>
                    </div>
                }
            >
                {popoverContent()}
            </UIPopover>
            {/* Dummy to get width of popover content */}
            <div
                style={{
                    position: 'absolute',
                    opacity: 0,
                    pointerEvents: 'none'
                }}
            >
                {popoverContent(divRef)}
            </div>
        </>
    );
}

function StatusCell(props: { device: Device }): ReactElement {
    const { device } = props;

    return <DeviceStatus device={device} />;
}

function FeatureCell(props: { device: Device }): ReactElement {
    const { device } = props;

    return <DeviceFeaturesIcons device={device} />;
}
